export const en = {
  title: 'Model Proxy',
  desc: 'Route specific provider/model pairs through HTTP/HTTPS/SOCKS5 proxies. Specificity: exact model > prefix "vendor-*" > "*". Optional purpose filter targets compaction/session-title calls.',
  enabled: 'Enable proxy routing',
  debug: 'Debug logging (host log)',
  rules: 'Rules (first match wins — exact before prefix before "*")',
  addRule: 'Add rule',
  provider: 'Provider',
  providerPlaceholder: 'e.g. openai',
  model: 'Model',
  modelPlaceholder: "e.g. gpt-4o-mini, vendor-* or *",
  purpose: 'Purpose filter',
  purposePlaceholder: 'empty = all purposes (e.g. compaction, session-title)',
  purposeAll: 'All purposes',
  credRef: 'Credential ref',
  credRefPlaceholder: 'entry name in the credentials service (optional)',
  headers: 'Extra headers (direct or proxy)',
  headersName: 'Header name',
  headersValue: 'Value',
  headerSource: 'Value source',
  headerSourceFixed: 'Fixed value',
  headerSourceSession: 'Current session ID',
  headerSourceProvider: 'Current provider',
  headerSourceModel: 'Current model',
  headerSourcePurpose: 'Current purpose',
  headerValueDynamic: 'Filled from the current request context',
  headersAdd: '＋ Add header',
  headersPreset: '＋ x-opencode-session',
  headersHint: 'Choose a fixed value or a request-context variable. Existing request headers win.',
  proxyHost: 'Proxy host',
  proxyHosts: 'Proxy hosts',
  proxyHostsHint: 'Reusable connection profiles. Rules select a host or Direct; headers stay editable per rule.',
  hostsEmpty: 'No proxy hosts yet — add one below, or choose Direct on a rule.',
  hostName: 'Host name',
  hostInUse: 'Host is used by a rule or fallback',
  missingHost: 'Missing host',
  addHost: '＋ Add proxy host',
  newHostName: 'New proxy',
  direct: 'Direct (no proxy)',
  proxyUrl: 'Proxy URL',
  proxyPlaceholder: 'socks5://127.0.0.1:1080 or http://127.0.0.1:7890',
  enabledShort: 'On',
  delete: 'Delete',
  defaultProxy: 'Fallback proxy (when no rule matches)',
  defaultPlaceholder: 'optional, e.g. http://127.0.0.1:7890',
  saving: 'Saving…',
  save: 'Save',
  saved: 'Saved',
  unavailable: 'Model proxy settings unavailable.',
  empty: 'No rules yet — add one above. Tip: use model "*" for all models of a provider.',
  invalid: 'Please fix highlighted fields.',
  duplicate: 'Duplicate provider+model',
  reading: 'Loading…',
  readOnly: 'Read-only (settings provider not writable).',
  choose: 'Select…',
  customOption: 'Custom…',
  backToList: 'Pick from the installed catalog',
  dormant: 'not configured',
  groupConfigured: 'Configured providers',
  groupDirectory: 'Other directory routes',
  showUnconfigured: 'Show unconfigured ({n})',
  proxyGhostHint: 'Type a prefix, then Tab completes it',
  wildcardAll: 'any model',
  catalogUnavailable: 'Provider catalog unavailable — type values manually.',
  modelsUnavailable: 'Model catalog unavailable — type model ids manually or use *.',
  creatorTitle: 'New rules',
  creatorModels: 'Models',
  creatorPatternHint: 'Or a pattern (vendor-* or *)',
  createN: 'Create {n} rule(s)',
  cancel: 'Cancel',
  alreadyRuled: 'rule exists',
  noneCatalogued: 'No catalog models — use * or a custom pattern.',
  groupApplyProxy: 'proxy for all…',
  groupApply: 'Apply',
  groupEnableAll: 'Enable all',
  groupDisableAll: 'Disable all',
  groupDelete: 'Delete group',
  groupConfirmDelete: 'Sure?',
} as const

export const zh = {
  title: '模型代理',
  desc: '按 provider/model 粒度走代理（HTTP/HTTPS/SOCKS5）。优先级：精确 model > 前缀 "vendor-*" > "*"。可选用途过滤可单独控制 compaction/session-title 等调用。',
  enabled: '启用代理路由',
  debug: '调试日志（Host 日志）',
  rules: '规则（首条命中即用 — 精确 > 前缀 > "*"）',
  addRule: '添加规则',
  provider: 'Provider',
  providerPlaceholder: '例如 openai',
  model: 'Model',
  modelPlaceholder: '例如 gpt-4o-mini、vendor-* 或 *',
  purpose: '用途过滤',
  purposePlaceholder: '留空=所有用途（如 compaction、session-title）',
  purposeAll: '所有用途',
  credRef: '凭据引用',
  credRefPlaceholder: '凭据服务中的条目名，可选',
  headers: '附加 Header（代理或直连均发送）',
  headersName: 'Header 名称',
  headersValue: 'Header 值',
  headerSource: '填写策略',
  headerSourceFixed: '固定值',
  headerSourceSession: '当前会话 Session ID',
  headerSourceProvider: '当前 Provider',
  headerSourceModel: '当前模型',
  headerSourcePurpose: '当前用途 Purpose',
  headerValueDynamic: '自动取当前请求上下文',
  headersAdd: '＋ 添加 Header',
  headersPreset: '＋ x-opencode-session',
  headersHint: '可选择固定值或请求上下文变量；已存在的同名 Header 优先。',
  proxyHost: '代理主机',
  proxyHosts: '代理主机',
  proxyHostsHint: '统一维护可复用的连接配置；上方规则只选择主机或直连，Header 仍按规则编辑。',
  hostsEmpty: '暂无代理主机——可在下方添加，或在规则中选择直连。',
  hostName: '主机名称',
  hostInUse: '该主机仍被规则或兜底配置使用',
  missingHost: '主机不存在',
  addHost: '＋ 添加代理主机',
  newHostName: '新代理',
  direct: '直连（不使用代理）',
  proxyUrl: '代理地址',
  proxyPlaceholder: 'socks5://127.0.0.1:1080 或 http://127.0.0.1:7890',
  enabledShort: '启用',
  delete: '删除',
  defaultProxy: '兜底代理（无规则命中时）',
  defaultPlaceholder: '可选，例如 http://127.0.0.1:7890',
  saving: '保存中…',
  save: '保存',
  saved: '已保存',
  unavailable: '模型代理设置不可用。',
  empty: '暂无规则 — 点击上方添加。提示：model 用 "*" 表示该 provider 的所有模型。',
  invalid: '请修正标红字段。',
  duplicate: 'provider+model 重复',
  reading: '加载中…',
  readOnly: '只读（settings provider 不可写）。',
  choose: '请选择…',
  customOption: '自定义…',
  backToList: '从已安装目录选择',
  dormant: '未配置',
  groupConfigured: '已配置的 Provider',
  groupDirectory: '目录中的其他路由',
  showUnconfigured: '显示未配置（{n}）',
  proxyGhostHint: '输入前缀后按 Tab 可补全',
  wildcardAll: '所有模型',
  catalogUnavailable: '提供方目录不可用 — 请手动输入。',
  modelsUnavailable: '模型目录不可用——请手动输入模型 ID 或使用 *。',
  creatorTitle: '新建规则',
  creatorModels: '勾选模型',
  creatorPatternHint: '或自定义模式（vendor-*、*）',
  createN: '创建 {n} 条规则',
  cancel: '取消',
  alreadyRuled: '已有规则',
  noneCatalogued: '该 provider 无目录模型——可用 * 或自定义模式。',
  groupApplyProxy: '整组代理地址…',
  groupApply: '应用',
  groupEnableAll: '全部启用',
  groupDisableAll: '全部停用',
  groupDelete: '删除本组',
  groupConfirmDelete: '确认？',
} as const

export type LocaleKey = keyof typeof en
